import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import moment from 'moment'
import { History } from 'history'
import classNames from 'classnames'

import AnomalyDiff from '../components/anomaly-diff'
import Button from '../components/button'
import Graph from '../components/graph'
import {
  switchAccountAsync,
  getSelectedAccountAsync,
  logActionAsync,
  getAnomalyDateDescription,
  getAllGaDimensions,
  updateAnomalyDescription,
  getAnomalyCausesAndSolutions,
  getExplainSetup,
  requestAnomalyGraphData,
  updateAnomalyDescriptionReducersOnly,
} from '../store/actions'
import SiteWrapper from '../components/sitewrapper'
import Layout from '../components/layout'
import Breakdown from '../components/anomaly-breakdown'
import AnomalyBreakdownPageLoader from '../components/anomaly-breakdown-page-loader'
import CausesAndSolutionsCarousels from '../components/causes-and-solutions-carousels'
import CustomCauseInput from '../components/custom-cause-input'
import TwoColumns, {
  Column,
  OuterBox,
  InnerBox,
} from '../components/two-columns'
import styles from '../component-styles/anomaly-breakdown.module.scss'
import Footer from '../components/footer'
import { TagsWrapper, Tag } from '../components/add-multi-values-tags'
import useMobile from '../hooks/useMobile'
import {
  getItemByKeyValue,
  getDimensionTitle,
  removeByKeyValue,
  useQuery,
} from '../helpers'
import ShareAnomalyButton from '../components/share-anomaly-button'

interface Props {
  match: any
  history: History
}

export default function AnomalyBreakdown({
  match,
  history,
}: Props): React.ReactElement {
  const dispatch = useDispatch()
  const query = useQuery()
  const [graphWidth, setGraphWidth] = useState(0)
  const [anomalyUpdated, setAnomalyUpdated] = useState(false)
  const [accountSwitched, setAccountSwitched] = useState(false)

  const { metricID, metricID: metric, anomDate } = match.params
  const { anomaly, dimensions, explain, site, user, anomalies } = useSelector(
    (s: any): any => s,
  )
  const { selected } = site
  const isMobile = useMobile(769)
  const data = anomalies[metric] ? anomalies[metric] : null
  const id = `${metric}/${anomDate}`
  const anomDateM = moment(anomDate, 'DD-MM-YYYY')
  const anomDateFormatted = anomDateM.format('YYYYMMDD')

  const {
    // anomalyDate,
    anomalyDateID,
    anomalyNotInteresting,
    selectedRelevantDimensions,
    accountName,
  } = anomaly?.description || {}

  useEffect((): void => {
    const aData = {
      metric,
      date: anomDateFormatted,
    }
    dispatch(getAnomalyDateDescription(aData))

    if (!dimensions.gaDimensions) {
      dispatch(getAllGaDimensions())
    }

    if (explain.pageLayout.length === 0) {
      dispatch(getExplainSetup())
    }

    if (!data) {
      const today = moment(new Date(Date.now()))
      const daysToToday = Math.abs(anomDateM.diff(today, 'days'))
      const t =
        !Number.isNaN(daysToToday) && daysToToday >= 15 ? 15 : daysToToday
      const f = 30 - t
      const from = moment(anomDate, 'DD-MM-YYYY')
        .subtract(f, 'days')
        .format('YYYYMMDD')
      const to = moment(anomDate, 'DD-MM-YYYY')
        .add(t, 'days')
        .format('YYYYMMDD')
      dispatch(requestAnomalyGraphData({ to, from, metricID }))
    }
    // eslint-disable-next-line
  }, [])

  useEffect((): void => {
    if (anomalyDateID) {
      dispatch(getAnomalyCausesAndSolutions({ id, anomalyDateID }))
    }
    // eslint-disable-next-line
  }, [anomalyDateID, selectedRelevantDimensions])

  // The site selector would normally load the data,
  // but since that is not present, it had to be triggered
  // automatically in this parent page
  useEffect((): void => {
    if (!selected) {
      dispatch(getSelectedAccountAsync())
    }
    // eslint-disable-next-line
  }, [selected])

  useEffect(() => {
    const accId = query?.get('accountID')

    if (!accId || !user?.details?.currentAccount || accountSwitched) {
      return
    }
    setAccountSwitched(true)

    const longDescription = JSON.stringify({ metric, anomDate })
    const trackingData = {
      action: 'email-click-through-anomaly',
      longDescription,
      websiteSection: 'anomaly',
    }
    dispatch(logActionAsync(trackingData))

    if (user.details.currentAccount === accId) {
      return
    }

    setAccountSwitched(true)
    dispatch(switchAccountAsync(accId))
    // eslint-disable-next-line
  }, [query, user])

  const isLoading =
    !anomaly ||
    !anomaly.description ||
    // anomDateFormatted !== anomalyDate ||
    data === null

  useEffect((): void => {
    if (!anomalyUpdated && !isLoading) {
      const fDate = moment(anomDate, 'DD-MM-YYYY').format('DD/MM/YYYY')
      const found = getItemByKeyValue(data.anomalyRange, 'date', fDate)
      if (found === -1) {
        setAnomalyUpdated(true)
        dispatch(
          updateAnomalyDescriptionReducersOnly({ ...anomaly.description }),
        )
      }
    }
    // eslint-disable-next-line
  }, [data, anomaly])
  const { authenticated } = user

  if (isLoading) {
    return (
      <AnomalyBreakdownPageLoader
        isMobile={isMobile}
        authenticated={authenticated}
      />
    )
  }

  const updateStatus = (anom: any) => {
    if (anomaly && anomaly.description !== null) {
      dispatch(updateAnomalyDescription({ ...anomaly.description, ...anom }))
    }
  }

  const {
    increaseSentimentPositive,
    userSuppliedCause,
    userSuppliedAction,
    userSuppliedBenefit,
    userSuppliedEffort,
  } = anomaly.description
  const rightEmpty =
    !authenticated &&
    selectedRelevantDimensions.length === 0 &&
    userSuppliedCause === '' &&
    userSuppliedAction === '' &&
    userSuppliedBenefit === '' &&
    userSuppliedEffort === ''
  return (
    <SiteWrapper greyAndWhite={!rightEmpty} grey={rightEmpty} noFooter>
      <Layout type="full-screen" width={1200}>
        <TwoColumns className={styles.columns}>
          <Column main equal transparent>
            <div className={styles.topPanel}>
              {authenticated && (
                <Link className={styles.backButton} to="/explain/">
                  All anomalies
                </Link>
              )}
              {!authenticated && (
                <Link className={styles.backButton} to="/login">
                  Login to explore
                </Link>
              )}
              {authenticated && (
                <>
                  {anomalyNotInteresting ? (
                    <Button
                      type="flatPink"
                      toolTip="This will show the anomaly on the graph. This also helps our model to learn."
                      align="right"
                      onClick={() => {
                        updateStatus({
                          anomalyNotInteresting: false,
                        })
                      }}
                    >
                      Interesting
                    </Button>
                  ) : (
                    <Button
                      type="flatBlue"
                      toolTip="This will hide the anomaly from graph. This also helps our model to learn."
                      align="right"
                      onClick={() => {
                        updateStatus({
                          anomalyNotInteresting: true,
                        })
                      }}
                    >
                      Not interesting
                    </Button>
                  )}
                </>
              )}
              <h1 className={styles.accountName}>Anomaly for {accountName}</h1>
              <h2 className={styles.pageTitle}>
                <AnomalyDiff
                  arrowOnly
                  invertPolarity={!increaseSentimentPositive}
                  actual={anomaly.description.metricActualValue}
                  expected={anomaly.description.metricExpectedValue}
                  compact
                  largeIcon
                />
                <span
                  className={styles.rowTitleDate}
                  dangerouslySetInnerHTML={{
                    __html: moment(anomDate, 'DD-MM-YYYY')
                      .format('ddd Do MMM YYYY')
                      .replace(/(\d)(st|nd|rd|th)/g, '$1<sup>$2</sup>'),
                  }}
                />
                <AnomalyDiff
                  inline
                  roundNumber
                  noIcon
                  invertPolarity={!increaseSentimentPositive}
                  actual={anomaly.description.metricActualValue}
                  expected={anomaly.description.metricExpectedValue}
                />
                <span className={styles.rowTitleDirection}>
                  {anomaly.description.metricActualValue >
                  anomaly.description.metricExpectedValue
                    ? 'more'
                    : 'less'}{' '}
                </span>
                <span className={styles.rowTitleName}>
                  {anomaly.description.displayName}{' '}
                </span>
                <span className={styles.rowTitleDirection}>than expected </span>
              </h2>
            </div>
            <div
              ref={(element) => {
                if (element) {
                  setGraphWidth(element.offsetWidth)
                }
              }}
              className={styles.mainContent}
            >
              {data && (
                <Graph
                  noInterraction
                  history={history}
                  increaseSentimentPositive={increaseSentimentPositive}
                  data={{
                    ...data,
                    anomalyRange: [
                      getItemByKeyValue(
                        data.anomalyRange,
                        'date',
                        moment(anomDate, 'DD-MM-YYYY').format('DD/MM/YYYY'),
                      ),
                    ],
                  }}
                  metricID={metric}
                  width={graphWidth}
                  metricName={anomaly.description.displayName}
                />
              )}
              <Breakdown
                increaseSentimentPositive={increaseSentimentPositive}
                metric={metric}
                dimensions={anomaly.description.dimensions}
                id={id}
                viewOnly={!authenticated}
                selected={selectedRelevantDimensions}
                onMetricClick={({ a, e, mDN, t, add }) => {
                  let copyDims = Array.isArray(selectedRelevantDimensions)
                    ? selectedRelevantDimensions.concat()
                    : []
                  const found = getItemByKeyValue(
                    selectedRelevantDimensions,
                    'mDN',
                    mDN,
                  )
                  if (add && found === -1) {
                    copyDims.push({ a, e, mDN, t })
                  } else if (!add && found !== -1) {
                    copyDims = removeByKeyValue(
                      selectedRelevantDimensions,
                      'mDN',
                      mDN,
                    )
                  } else {
                    return
                  }
                  updateStatus({
                    selectedRelevantDimensions: copyDims,
                  })
                }}
              />
            </div>
            {!isMobile && <Footer className={styles.footer} />}
          </Column>
          <Column side equal transparent>
            <OuterBox
              panel
              className={classNames({ [styles.emptyColumn]: rightEmpty })}
            >
              {!rightEmpty && (
                <>
                  <InnerBox className={styles.topBox}>
                    <div className={styles.titleWithButton}>
                      <h1 className={styles.topTitle}>Insights</h1>
                      {authenticated && <ShareAnomalyButton />}
                    </div>
                    <h3 className={styles.dimensionsTitle}>
                      Interesting dimensions
                    </h3>
                    {Array.isArray(selectedRelevantDimensions) &&
                      selectedRelevantDimensions.length > 0 && (
                        <TagsWrapper noBorder className={styles.tagsWrapper}>
                          <>
                            {selectedRelevantDimensions.map((item) => {
                              return (
                                <Tag key={item.mDN} type="blue">
                                  <AnomalyDiff
                                    invertPolarity={!increaseSentimentPositive}
                                    arrowOnly
                                    actual={item.a}
                                    expected={item.e}
                                  />
                                  <span>
                                    {getDimensionTitle(
                                      item.mDN,
                                      dimensions.gaDimensions,
                                    )}
                                  </span>
                                </Tag>
                              )
                            })}
                          </>
                        </TagsWrapper>
                      )}
                    <CustomCauseInput viewOnly={!authenticated} />
                  </InnerBox>
                  <div className={styles.bottomPanel}>
                    <InnerBox>
                      <h2 className={styles.panelTitle}>
                        Auto-generated by uplifter
                      </h2>
                      <CausesAndSolutionsCarousels viewOnly={!authenticated} />
                    </InnerBox>
                  </div>
                </>
              )}
            </OuterBox>
            <div className={styles.bottomPanel}>
              {isMobile && <Footer className={styles.footer} />}
            </div>
          </Column>
        </TwoColumns>
      </Layout>
    </SiteWrapper>
  )
}
